import React, { useEffect } from "react";

import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { ReportGeneratorIcon } from "assets/material-custom-icons";
import { usePrompt } from "hooks";
import { useBulkBatchesSelector, useBulkUploadActions } from "store";
import { BulkFileExtractionStatus, IBulkBatch, IBulkBatchFile } from "store/types";
import { toastify } from "utils/tyostify";

import { BulkUploadReview } from "./Bulk-upload-review/BulkUploadReview";

interface Props {
  isMinimized?: boolean;
  onCancel?: () => void;
}

const STATUS_LABEL: Record<BulkFileExtractionStatus, string> = {
  queued: "Queued",
  extracting: "AI Extraction...",
  done: "Done",
  failed: "Failed",
  unsupported: "Unsupported",
  cancelled: "Cancelled"
};

const StatusIcon: React.FC<{ status: BulkFileExtractionStatus }> = ({ status }) => {
  switch (status) {
    case "queued":
      return <CircularProgress size={16} />;
    case "extracting":
      return <ReportGeneratorIcon sx={{ color: "#496682", fontSize: 22 }} />;
    case "done":
      return <CheckCircleIcon sx={{ color: "#07A27D", fontSize: 22 }} />;
    case "failed":
      return <ErrorOutlineIcon sx={{ color: "#D32F2F", fontSize: 20 }} />;
    default:
      return <BlockIcon sx={{ color: "#7b92a9", fontSize: 20 }} />;
  }
};

export const BulkUploadWidget: React.FC<Props> = ({ isMinimized, onCancel }) => {
  const batches = useBulkBatchesSelector();
  const { cancelBulkUploadBatchAction } = useBulkUploadActions();
  const openPrompt = usePrompt();

  const batchList = Object.values(batches);
  const isProcessing = batchList.some((b) => b.batch_status === "in_progress");
  const totalFiles = batchList.reduce((acc, b) => acc + b.files.length, 0);

  useEffect(() => {
    if (!batchList.length) onCancel?.();
  }, [batchList.length, onCancel]);

  const handleCancel = async (batch: IBulkBatch) => {
    try {
      await cancelBulkUploadBatchAction(batch.upload_batch_id);
    } catch {
      toastify({ message: "Nothing to cancel.", type: "warn" });
    }
  };

  const handleReview = (batch: IBulkBatch) => {
    openPrompt({
      componentType: "fullscreen",
      Component: BulkUploadReview,
      props: { batchId: batch.upload_batch_id }
    });
  };

  if (isMinimized) {
    return (
      <Stack direction="row" alignItems="center" gap="8px" sx={{ px: "16px", py: "10px" }}>
        {isProcessing ? <CircularProgress size={14} /> : <CheckCircleIcon sx={{ color: "#07A27D", fontSize: 16 }} />}
        <Typography sx={{ fontSize: 12, color: "#496682" }}>
          {isProcessing ? "Processing" : "Completed"} {totalFiles} file{totalFiles === 1 ? "" : "s"}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack sx={{ maxHeight: 420, overflowY: "auto" }}>
      {batchList.map((batch) => (
        <BatchSection
          key={batch.upload_batch_id}
          batch={batch}
          onCancel={() => handleCancel(batch)}
          onReview={() => handleReview(batch)}
        />
      ))}
    </Stack>
  );
};

const BatchSection: React.FC<{ batch: IBulkBatch; onCancel: () => void; onReview: () => void }> = ({
  batch,
  onCancel,
  onReview
}) => {
  const isAwaitingReview = batch.batch_status === "awaiting_review";

  return (
    <Box>
      <Stack direction="row" alignItems="center" gap="6px" sx={{ bgcolor: "#f6f8fc", px: "10px", py: "4px" }}>
        <Typography sx={{ flex: 1, fontSize: 14, color: "#071f35" }}>{batch.deal_name || "Extracting..."}</Typography>
        {isAwaitingReview ? (
          <Button
            variant="contained"
            size="sm"
            onClick={onReview}
            sx={{ bgcolor: "#071f35", borderRadius: "32px", textTransform: "none", px: "12px", py: "2px" }}
          >
            Review
          </Button>
        ) : (
          <Button
            variant="outlined"
            size="sm"
            onClick={onCancel}
            sx={{
              borderColor: "#7b92a9",
              color: "#071f35",
              borderRadius: "32px",
              textTransform: "none",
              px: "12px",
              py: "2px"
            }}
          >
            Cancel
          </Button>
        )}
      </Stack>
      <Stack sx={{ px: "16px" }}>
        {batch.files.map((file) => (
          <FileRow key={file.file_id} file={file} />
        ))}
      </Stack>
    </Box>
  );
};

const FileRow: React.FC<{ file: IBulkBatchFile }> = ({ file }) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ py: "10px" }} gap="10px">
    <Typography noWrap sx={{ fontSize: 16, color: "#071f35", overflow: "hidden", textOverflow: "ellipsis" }}>
      {file.file_name}
    </Typography>
    <Stack direction="row" alignItems="center" gap="10px" sx={{ flexShrink: 0 }}>
      <Typography sx={{ fontSize: 14, color: "#496682", whiteSpace: "nowrap" }}>{STATUS_LABEL[file.status]}</Typography>
      <StatusIcon status={file.status} />
    </Stack>
  </Stack>
);
